import React, { useEffect, useState } from "react";
import Link from "next/link";

/**
 * Global paywall: mounted once in _app, it wraps window.fetch and pops an
 * upgrade prompt whenever any API call returns HTTP 402 (plan limit hit).
 * The CTA goes to the in-app billing section (real Stripe checkout);
 * the marketing pricing page is the compare-plans secondary.
 */

type Wall = {
  error?: string;
  feature?: string;
  limit?: number;
  plan?: string;
  upgradeUrl?: string;
};

const FEATURE_LABEL: Record<string, string> = {
  automations: "flows",
  maps: "maps",
  usersPerCompany: "team members",
  companiesPerUser: "workspaces",
  contacts: "contacts",
  nodesPerFlow: "nodes in one flow",
  looseNodes: "free-floating map nodes",
};

export default function PaywallHost() {
  const [wall, setWall] = useState<Wall | null>(null);

  useEffect(() => {
    const w = window as unknown as { __frazzlePaywallPatched?: boolean };
    if (!w.__frazzlePaywallPatched) {
      w.__frazzlePaywallPatched = true;
      const orig = window.fetch.bind(window);
      window.fetch = async (...args: Parameters<typeof fetch>) => {
        const res = await orig(...args);
        if (res.status === 402) {
          try {
            const body = await res.clone().json();
            if (body?.paywall)
              window.dispatchEvent(
                new CustomEvent("frazzle:paywall", { detail: body })
              );
          } catch {
            /* not a paywall payload */
          }
        }
        return res;
      };
    }
    const onWall = (e: Event) => setWall((e as CustomEvent).detail as Wall);
    window.addEventListener("frazzle:paywall", onWall);
    return () => window.removeEventListener("frazzle:paywall", onWall);
  }, []);

  if (!wall) return null;
  const featureLabel = FEATURE_LABEL[wall.feature ?? ""] ?? "this";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setWall(null);
      }}
    >
      <div className="w-[min(440px,94vw)] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="bg-gradient-to-br from-indigo-600 to-violet-600 px-6 py-5 text-white">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-indigo-200">
            Plan limit reached
          </div>
          <div className="mt-1 text-[18px] font-bold leading-snug">
            You&apos;ve hit the {wall.plan === "free" ? "Free" : wall.plan} plan
            limit for {featureLabel}
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-[13px] leading-relaxed text-slate-600">
            {wall.error ??
              "Upgrade your plan to keep building — everything you've made stays exactly where it is."}
          </p>
          <ul className="mt-3 space-y-1.5 text-[13px] text-slate-600">
            <li>✓ More flows, nodes, maps, contacts and teammates</li>
            <li>✓ AI access (MCP) &amp; public API on Pro</li>
            <li>✓ Everything you&apos;ve built comes with you</li>
          </ul>
          <div className="mt-5 flex items-center gap-2">
            <Link
              href="/company"
              className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-center text-[13px] font-semibold text-white hover:bg-indigo-500"
            >
              Upgrade now
            </Link>
            <a
              href="https://www.frazzlecrm.com/pricing"
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-medium text-slate-600 hover:bg-slate-100"
            >
              Compare plans
            </a>
            <button
              onClick={() => setWall(null)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-medium text-slate-600 hover:bg-slate-100"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
